import React from 'react';
import { ErrorBoundary } from '../ErrorBoundary';
import { Button } from '../../ui/Button';
import Link from 'next/link';

interface EventErrorFallbackProps {
  error?: Error;
  errorInfo?: React.ErrorInfo;
  retry?: () => void;
}

/**
 * Event-specific error fallback UI
 */
const EventErrorFallback: React.FC<EventErrorFallbackProps> = ({ error, retry }) => {
  const handleBrowseEvents = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/events';
    }
  };

  const handleGoHome = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  const handleClearFilters = () => {
    if (typeof window !== 'undefined') {
      // Clear any stored event filters/search
      try {
        localStorage.removeItem('event-filters');
        localStorage.removeItem('event-search');
        sessionStorage.removeItem('event-scroll-position');
      } catch (e) {
        // Ignore localStorage errors
      }

      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-lg rounded-lg sm:px-10">
          <div className="text-center">
            {/* Event Error Icon */}
            <div className="mx-auto h-16 w-16 text-purple-600 mb-6">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Event Page Error
            </h2>

            <p className="text-gray-600 mb-6 text-sm">
              We encountered an issue loading the event information.
              This might be a temporary problem with the event data.
            </p>

            {/* Action Buttons */}
            <div className="space-y-3 mb-6">
              {retry && (
                <Button
                  onClick={retry}
                  variant="solid"
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  Try Again
                </Button>
              )}

              <Button
                onClick={handleBrowseEvents}
                variant="secondary"
                className="w-full"
              >
                Browse All Events
              </Button>

              <Button
                onClick={handleClearFilters}
                variant="outline"
                className="w-full"
              >
                Clear Filters & Refresh
              </Button>

              <Button
                onClick={handleGoHome}
                variant="outline"
                className="w-full text-gray-600 border-gray-300 hover:bg-gray-50"
              >
                Go to Homepage
              </Button>
            </div>

            {/* Alternative Actions */}
            <div className="border-t border-gray-200 pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Looking for tickets?</h3>
              <div className="space-y-2 text-sm">
                <Link
                  href="/events"
                  className="block text-blue-600 hover:text-blue-800 hover:underline"
                >
                  All Upcoming Events
                </Link>
                <Link
                  href="/information"
                  className="block text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Venue Information
                </Link>
                <Link
                  href="/signup"
                  className="block text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Create Account
                </Link>
              </div>
            </div>

            {/* Error Details for Development */}
            {process.env.NODE_ENV === 'development' && error && (
              <details className="mt-6 text-left bg-gray-50 p-4 rounded text-xs">
                <summary className="cursor-pointer font-medium text-gray-700">
                  Error Details (Development Only)
                </summary>
                <pre className="mt-2 whitespace-pre-wrap text-xs text-gray-600">
                  {error.message}
                  {error.stack}
                </pre>
              </details>
            )}

            {/* Support Information */}
            <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500">
              <p className="mb-1">
                <strong>Need Help Finding an Event?</strong>
              </p>
              <p>
                If you&apos;re looking for a specific show, please{' '}
                <a
                  href="mailto:support@dramatickets.co.uk"
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  contact our support team
                </a>
                {' '}with the event details.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Event Error Boundary Component
 * Specialized for event pages and ticket purchasing flow
 */
export const EventErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ErrorBoundary
      fallback={EventErrorFallback}
      onError={(error, errorInfo) => {
        // Log event-specific errors
        console.error('[EventErrorBoundary] Event page error:', error);
        console.error('[EventErrorBoundary] Error info:', errorInfo);

        // Send to monitoring service with event context
        if (typeof window !== 'undefined' && (window as any).gtag) {
          (window as any).gtag('event', 'exception', {
            description: `Event Error: ${error.message}`,
            fatal: false,
            custom_map: { event_error: true }
          });
        }
      }}
      errorTitle="Event Page Error"
      errorMessage="We encountered an issue loading this event. Please try again or browse other events."
      maxRetries={2}
      showErrorDetails={process.env.NODE_ENV === 'development'}
    >
      {children}
    </ErrorBoundary>
  );
};
